import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Beaker, ClipboardCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  requested: "bg-[#FEEBC8] text-[#7B341E]",
  in_progress: "bg-[#BEE3F8] text-[#2A4365]",
  completed: "bg-[#C6F6D5] text-[#22543D]",
  cancelled: "bg-[#FED7D7] text-[#742A2A]",
};

export default function KessServiceCenter() {
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ subject_name: "", subject_email: "", subject_phone: "", notes: "", linked_referral_id: "" });

  const canRequest = useMemo(
    () => user && ["provider", "case_manager", "agency_owner", "agency_staff", "admin", "super_admin"].includes(user.role),
    [user],
  );

  const load = useCallback(async () => {
    const [s, mine] = await Promise.all([
      api.get("/kess/services"),
      canRequest ? api.get("/kess/requests/mine") : Promise.resolve({ data: [] }),
    ]);
    setServices(s.data);
    setRequests(mine.data || []);
  }, [canRequest]);

  useEffect(() => { load(); }, [load]);

  const openRequest = (svc) => {
    if (!user) { toast.error("Sign in to request services"); return; }
    setActive(svc);
    setForm({ subject_name: "", subject_email: "", subject_phone: "", notes: "", linked_referral_id: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.subject_name) { toast.error("Subject name required"); return; }
    try {
      await api.post("/kess/requests", { service_key: active.key, ...form });
      toast.success("Request submitted. An admin will follow up shortly.");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    }
  };

  return (
    <div data-testid="kess-page">
      <section className="border-b border-[#D1D5DB] shv-grain">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
          <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-3">Cleared by Kess Service Center</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-[#0A192F] leading-[1.05] max-w-3xl">
            Verification, testing, and screening — <span className="text-[#1E3A8A]">in one place.</span>
          </h1>
          <p className="mt-6 text-lg text-[#4A5568] max-w-2xl">
            Providers, agencies, and case managers can request verification and testing services directly from the dashboard. Pricing is transparent; admins manage fulfillment.
          </p>
        </div>
      </section>

      <Tabs defaultValue="catalog" className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
        <TabsList>
          <TabsTrigger value="catalog" data-testid="kess-tab-catalog">Service Catalog</TabsTrigger>
          {canRequest && <TabsTrigger value="mine" data-testid="kess-tab-mine">My Requests ({requests.length})</TabsTrigger>}
        </TabsList>

        <TabsContent value="catalog">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc) => (
              <div key={svc.key} data-testid={`kess-service-${svc.key}`} className="bg-white border border-[#D1D5DB] p-6 rounded-sm shv-card-hover">
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-sm bg-[#F5F5F0] border border-[#D1D5DB] flex items-center justify-center text-[#1E3A8A]">
                    <Beaker className="w-5 h-5" />
                  </div>
                  <span className="font-heading font-bold text-2xl text-[#C05621]">${svc.price.toFixed(2)}</span>
                </div>
                <div className="font-heading font-semibold text-[#0A192F] mt-4 text-lg">{svc.name}</div>
                <div className="text-sm text-[#4A5568] mt-2">{svc.description}</div>
                <div className="mt-5 flex gap-2 flex-wrap">
                  {canRequest ? (
                    <Button data-testid={`kess-request-${svc.key}`} onClick={() => openRequest(svc)} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">
                      <ClipboardCheck className="w-4 h-4 mr-1.5" /> Request Service
                    </Button>
                  ) : (
                    <Link to={user ? "/dashboard" : "/register?role=provider"}>
                      <Button variant="outline" className="border-[#0A192F] text-[#0A192F]">Provider / Agency only</Button>
                    </Link>
                  )}
                  {svc.payment_link && (
                    <a href={svc.payment_link} target="_blank" rel="noreferrer">
                      <Button variant="outline" data-testid={`kess-paylink-${svc.key}`}>Pay <ExternalLink className="w-3.5 h-3.5 ml-1.5" /></Button>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {canRequest && (
          <TabsContent value="mine">
            {requests.length === 0 ? (
              <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
                <div className="font-heading font-semibold text-[#0A192F]">No service requests yet</div>
                <div className="text-sm text-[#4A5568] mt-1">Request a service from the catalog above to start tracking it here.</div>
              </div>
            ) : (
              <div className="bg-white border border-[#D1D5DB] rounded-sm divide-y divide-[#D1D5DB]">
                {requests.map((r) => (
                  <div key={r.id} data-testid={`kess-mine-${r.id}`} className="p-4 flex items-center gap-3 justify-between flex-wrap">
                    <div>
                      <div className="font-medium text-[#0A192F]">{r.service_name} for {r.subject_name}</div>
                      <div className="text-xs text-[#4A5568]">${r.price?.toFixed?.(2) || r.price} · Submitted {new Date(r.created_at).toLocaleDateString()}</div>
                      {r.admin_note && <div className="text-xs text-[#0A192F] mt-1">Admin: {r.admin_note}</div>}
                    </div>
                    <Badge className={STATUS_COLOR[r.status]}>{r.status.replace("_", " ")}</Badge>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request — {active?.name}</DialogTitle>
            <DialogDescription>Submit this request to the Cleared by Kess Service Center. An admin will follow up with next steps and a payment link if applicable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="block mb-1.5">Subject name (person being tested/verified)</Label><Input data-testid="kess-subject-name" value={form.subject_name} onChange={(e) => setForm({ ...form, subject_name: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="block mb-1.5">Subject email</Label><Input data-testid="kess-subject-email" value={form.subject_email} onChange={(e) => setForm({ ...form, subject_email: e.target.value })} /></div>
              <div><Label className="block mb-1.5">Subject phone</Label><Input data-testid="kess-subject-phone" value={form.subject_phone} onChange={(e) => setForm({ ...form, subject_phone: e.target.value })} /></div>
            </div>
            <div><Label className="block mb-1.5">Linked referral ID (optional)</Label><Input data-testid="kess-linked-ref" value={form.linked_referral_id} onChange={(e) => setForm({ ...form, linked_referral_id: e.target.value })} /></div>
            <div><Label className="block mb-1.5">Notes</Label><Textarea data-testid="kess-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div className="text-xs text-[#4A5568] bg-[#F5F5F0] p-3 rounded-sm">Price: <strong>${active?.price?.toFixed?.(2)}</strong>. An admin will reach out with next steps and (if applicable) a payment link.</div>
          </div>
          <DialogFooter>
            <Button data-testid="kess-submit" onClick={submit} className="bg-[#1E3A8A] hover:bg-[#1A365D] text-white">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
